import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../api';
import { 
  DollarSign, TrendingUp, TrendingDown, AlertTriangle, Bell, 
  FileText, Download, PieChart, BarChart3, Target, Wallet,
  Plus, RefreshCw, CheckCircle2, XCircle, Clock, AlertCircle,
  Settings, Trash2, Edit, Eye, ChevronRight, FileSpreadsheet,
  Receipt, Send, CreditCard, Sparkles, Copy, Upload
} from 'lucide-react';

const FinanceDashboardPage = () => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [triggeredAlerts, setTriggeredAlerts] = useState([]);
  const [cashFlow, setCashFlow] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Accounts Receivable state
  const [arData, setArData] = useState(null);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    due_date: '',
    payment_terms: 'net_30',
    tax_rate: 0,
    notes: ''
  });
  
  // Budget Templates state
  const [budgetTemplates, setBudgetTemplates] = useState([]);
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [showAIBudgetDialog, setShowAIBudgetDialog] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [newBudgetForm, setNewBudgetForm] = useState({
    name: '',
    description: '',
    template_type: 'general',
    fiscal_year: new Date().getFullYear(),
    is_template: false
  });
  const [aiBudgetForm, setAiBudgetForm] = useState({
    budget_type: 'marketing',
    fiscal_year: new Date().getFullYear(),
    total_budget: 100000,
    company_size: 'medium',
    industry: '',
    priorities: [],
    notes: ''
  });
  const [generatedBudget, setGeneratedBudget] = useState(null);
  
  // Alert state
  const [showNewAlertDialog, setShowNewAlertDialog] = useState(false);
  const [newAlert, setNewAlert] = useState({
    name: '',
    description: '',
    alert_type: 'revenue',
    threshold_percentage: 10,
    threshold_direction: 'both',
    check_frequency: 'weekly',
    is_active: true,
    recipients: []
  });
  const [checkingAlerts, setCheckingAlerts] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [runningScheduledCheck, setRunningScheduledCheck] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, alertsRes, triggeredRes, schedulerRes] = await Promise.all([
        api.get('/finance/dashboard'),
        api.get('/finance/variance-alerts'),
        api.get('/finance/variance-alerts/triggered?acknowledged=false'),
        api.get('/finance/variance-alerts/scheduler-status')
      ]);
      setDashboard(dashRes.data);
      setAlerts(alertsRes.data);
      setTriggeredAlerts(triggeredRes.data);
      setSchedulerStatus(schedulerRes.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Failed to load finance dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCashFlow = useCallback(async () => {
    try {
      const now = new Date();
      const startMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const res = await api.get(`/finance/cash-flow?start_month=${startMonth}&months=12`);
      setCashFlow(res.data);
    } catch (error) {
      console.error('Error fetching cash flow:', error);
    }
  }, []);

  const fetchARData = useCallback(async () => {
    try {
      const res = await api.get('/finance/accounts-receivable');
      setArData(res.data);
    } catch (error) {
      console.error('Error fetching AR data:', error);
    }
  }, []);

  const fetchBudgetTemplates = useCallback(async () => {
    try {
      const res = await api.get('/finance/budget-templates');
      setBudgetTemplates(res.data);
    } catch (error) {
      console.error('Error fetching budget templates:', error);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
    fetchCashFlow();
    fetchARData();
    fetchBudgetTemplates();
  }, [fetchDashboard, fetchCashFlow, fetchARData, fetchBudgetTemplates]);

  // Accounts Receivable handlers
  const handleCreateInvoice = async () => {
    if (!selectedOrder) return;
    try {
      await api.post('/finance/invoices', {
        order_id: selectedOrder.id,
        ...invoiceForm
      });
      toast.success('Invoice created successfully');
      setShowInvoiceDialog(false);
      setSelectedOrder(null);
      setInvoiceForm({ due_date: '', payment_terms: 'net_30', tax_rate: 0, notes: '' });
      fetchARData();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const handleSendInvoice = async (invoiceId) => {
    try {
      await api.post(`/finance/invoices/${invoiceId}/send`);
      toast.success('Invoice marked as sent');
      fetchARData();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const handleRecordPayment = async (invoiceId, amount) => {
    try {
      await api.put(`/finance/invoices/${invoiceId}`, {
        payment_received: amount,
        payment_date: new Date().toISOString().split('T')[0]
      });
      toast.success('Payment recorded');
      fetchARData();
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  // Budget Template handlers
  const handleGenerateAIBudget = async () => {
    try {
      setGeneratingAI(true);
      const res = await api.post('/finance/budget-templates/ai-generate', aiBudgetForm);
      setGeneratedBudget(res.data);
      toast.success('AI budget generated successfully');
    } catch (error) {
      toast.error('Failed to generate AI budget');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleSaveGeneratedBudget = async () => {
    if (!generatedBudget?.generated_budget) return;
    try {
      await api.post('/finance/budget-templates', generatedBudget.generated_budget);
      toast.success('Budget template saved');
      setShowAIBudgetDialog(false);
      setGeneratedBudget(null);
      fetchBudgetTemplates();
    } catch (error) {
      toast.error('Failed to save budget');
    }
  };

  const handleCreateBudget = async () => {
    if (!newBudgetForm.name) {
      toast.error('Please enter a budget name');
      return;
    }
    try {
      await api.post('/finance/budget-templates', {
        ...newBudgetForm,
        line_items: []
      });
      toast.success('Budget created successfully');
      setShowBudgetDialog(false);
      setNewBudgetForm({
        name: '',
        description: '',
        template_type: 'general',
        fiscal_year: new Date().getFullYear(),
        is_template: false
      });
      fetchBudgetTemplates();
    } catch (error) {
      toast.error('Failed to create budget');
    }
  };

  const handleExportBudgetExcel = async (templateId) => {
    try {
      const res = await api.get(`/finance/budget-templates/${templateId}/export/excel`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `budget_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Budget exported to Excel');
    } catch (error) {
      toast.error('Failed to export budget');
    }
  };

  const handleDownloadBudgetTemplate = async () => {
    try {
      const res = await api.get(`/finance/budget-templates/import/template?fiscal_year=${new Date().getFullYear()}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `budget_import_template.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleDeleteBudget = async (budgetId) => {
    if (!window.confirm('Delete this budget?')) return;
    try {
      await api.delete(`/finance/budget-templates/${budgetId}`);
      toast.success('Budget deleted');
      fetchBudgetTemplates();
    } catch (error) {
      toast.error('Failed to delete budget');
    }
  };

  const handleCreateAlert = async () => {
    try {
      await api.post('/finance/variance-alerts', newAlert);
      toast.success('Variance alert created');
      setShowNewAlertDialog(false);
      setNewAlert({
        name: '',
        description: '',
        alert_type: 'revenue',
        threshold_percentage: 10,
        threshold_direction: 'both',
        check_frequency: 'weekly',
        is_active: true,
        recipients: []
      });
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to create alert');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('Delete this alert?')) return;
    try {
      await api.delete(`/finance/variance-alerts/${alertId}`);
      toast.success('Alert deleted');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const handleCheckAlerts = async () => {
    try {
      setCheckingAlerts(true);
      const res = await api.post('/finance/variance-alerts/check');
      toast.success(`Checked ${res.data.alerts_checked} alerts, ${res.data.alerts_triggered} triggered`);
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to check alerts');
    } finally {
      setCheckingAlerts(false);
    }
  };

  const handleRunScheduledCheck = async () => {
    try {
      setRunningScheduledCheck(true);
      const res = await api.post('/finance/variance-alerts/run-scheduled-check');
      if (res.data.triggered > 0) {
        toast.success(`Scheduled check complete: ${res.data.triggered} alert(s) triggered, ${res.data.emails_sent} email(s) sent`);
      } else {
        toast.info(res.data.message || 'No alerts due for checking');
      }
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to run scheduled check');
    } finally {
      setRunningScheduledCheck(false);
    }
  };

  const handleAcknowledgeAlert = async (triggerId) => {
    try {
      await api.post(`/finance/variance-alerts/triggered/${triggerId}/acknowledge`);
      toast.success('Alert acknowledged');
      fetchDashboard();
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleDownloadForecastPdf = async () => {
    if (!dashboard?.active_forecast?.id) {
      toast.error('No active forecast to export');
      return;
    }
    try {
      setGeneratingPdf(true);
      const res = await api.get(`/finance/reports/forecast-pdf/${dashboard.active_forecast.id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `forecast_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleDownloadVariancePdf = async () => {
    try {
      setGeneratingPdf(true);
      const res = await api.get('/finance/reports/variance-pdf', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `variance_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      case 'low': return 'bg-blue-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="finance-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="finance-dashboard">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finance Hub</h1>
          <p className="text-gray-500">Financial Planning & Analysis</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            onClick={handleDownloadForecastPdf}
            disabled={generatingPdf || !dashboard?.active_forecast?.id}
            data-testid="download-forecast-pdf-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Forecast PDF
          </Button>
        </div>
      </div>

      {/* Alert Banner */}
      {triggeredAlerts.length > 0 && (
        <div className={`rounded-lg p-4 flex items-center justify-between ${
          triggeredAlerts.some(t => t.severity === 'critical') ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
        }`} data-testid="alert-banner">
          <div className="flex items-center gap-3">
            <AlertTriangle className={`h-5 w-5 ${
              triggeredAlerts.some(t => t.severity === 'critical') ? 'text-red-600' : 'text-amber-600'
            }`} />
            <span className="font-medium">
              {triggeredAlerts.length} unacknowledged variance alert{triggeredAlerts.length !== 1 ? 's' : ''} require attention
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setActiveTab('alerts')}>
            View Alerts <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card data-testid="mtd-revenue-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">MTD Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboard?.summary?.mtd_revenue)}</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="ytd-revenue-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">YTD Revenue</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboard?.summary?.ytd_revenue)}</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="budget-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Budget Utilized</p>
                <p className="text-2xl font-bold">{formatCurrency(dashboard?.summary?.budget_utilized)}</p>
                <p className="text-xs text-gray-400">of {formatCurrency(dashboard?.summary?.total_budget)}</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                <Wallet className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="alerts-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active Alerts</p>
                <p className="text-2xl font-bold">{dashboard?.alerts?.triggered_count || 0}</p>
                <p className="text-xs text-red-500">
                  {dashboard?.alerts?.critical_count || 0} critical, {dashboard?.alerts?.high_count || 0} high
                </p>
              </div>
              <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center">
                <Bell className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <PieChart className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Gross Margin</p>
                <p className="text-xl font-semibold">{dashboard?.metrics?.gross_margin || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <BarChart3 className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Operating Margin</p>
                <p className="text-xl font-semibold">{dashboard?.metrics?.operating_margin || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Budget Variance</p>
                <p className={`text-xl font-semibold ${(dashboard?.metrics?.budget_variance || 0) < 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(dashboard?.metrics?.budget_variance || 0) > 0 ? '+' : ''}{dashboard?.metrics?.budget_variance || 0}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="ar" data-testid="tab-ar">Accounts Receivable</TabsTrigger>
          <TabsTrigger value="budgets" data-testid="tab-budgets">Budget Templates</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            Variance Alerts
            {triggeredAlerts.length > 0 && (
              <Badge className="ml-2 bg-red-500">{triggeredAlerts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="cashflow" data-testid="tab-cashflow">Cash Flow</TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Active Forecast */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Active Forecast</CardTitle>
              </CardHeader>
              <CardContent>
                {dashboard?.active_forecast?.id ? (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Name</span>
                      <span className="font-medium">{dashboard.active_forecast.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Period</span>
                      <span className="font-medium">{dashboard.active_forecast.period}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Pipeline Value</span>
                      <span className="font-medium">{formatCurrency(dashboard.summary?.pipeline_value)}</span>
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => window.location.href = '/sales/forecasting'}
                    >
                      View Full Forecast
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <Target className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>No active forecast</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => window.location.href = '/sales/forecasting'}
                    >
                      Create Forecast
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Recent Triggered Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Alerts</CardTitle>
              </CardHeader>
              <CardContent>
                {triggeredAlerts.length > 0 ? (
                  <div className="space-y-3">
                    {triggeredAlerts.slice(0, 5).map(trigger => (
                      <div key={trigger.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Badge className={getSeverityColor(trigger.severity)}>
                            {trigger.severity}
                          </Badge>
                          <span className="text-sm">{trigger.alert_name}</span>
                        </div>
                        <span className="text-sm font-medium">
                          {trigger.variance_percentage > 0 ? '+' : ''}{trigger.variance_percentage}%
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-300" />
                    <p>All clear - no active alerts</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Accounts Receivable Tab */}
        <TabsContent value="ar" className="space-y-4">
          {/* AR Summary */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total AR</p>
                    <p className="text-2xl font-bold">{formatCurrency(arData?.summary?.total_ar)}</p>
                  </div>
                  <Receipt className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Orders Pending Invoice</p>
                    <p className="text-2xl font-bold">{arData?.summary?.orders_pending_invoice || 0}</p>
                  </div>
                  <FileText className="h-8 w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Invoices Pending</p>
                    <p className="text-2xl font-bold">{arData?.summary?.invoices_pending || 0}</p>
                  </div>
                  <Send className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Invoices Overdue</p>
                    <p className="text-2xl font-bold text-red-600">{arData?.summary?.invoices_overdue || 0}</p>
                  </div>
                  <AlertCircle className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* AR Aging */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">AR Aging Report</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-5 gap-4 text-center">
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-500">Current</p>
                  <p className="text-xl font-bold text-green-600">{formatCurrency(arData?.aging?.current)}</p>
                </div>
                <div className="p-4 bg-yellow-50 rounded-lg">
                  <p className="text-sm text-gray-500">1-30 Days</p>
                  <p className="text-xl font-bold text-yellow-600">{formatCurrency(arData?.aging?.days_30)}</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-gray-500">31-60 Days</p>
                  <p className="text-xl font-bold text-orange-600">{formatCurrency(arData?.aging?.days_60)}</p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-500">61-90 Days</p>
                  <p className="text-xl font-bold text-red-500">{formatCurrency(arData?.aging?.days_90)}</p>
                </div>
                <div className="p-4 bg-red-100 rounded-lg">
                  <p className="text-sm text-gray-500">90+ Days</p>
                  <p className="text-xl font-bold text-red-700">{formatCurrency(arData?.aging?.over_90)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Orders Pending Invoice */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Orders Ready for Invoicing</CardTitle>
              <CardDescription>Sales orders pending invoice creation</CardDescription>
            </CardHeader>
            <CardContent>
              {arData?.orders?.length > 0 ? (
                <div className="space-y-3">
                  {arData.orders.slice(0, 10).map(order => (
                    <div key={order.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{order.order_number}</p>
                        <p className="text-sm text-gray-500">
                          {order.items?.length || 0} item(s) • {order.status}
                        </p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-bold">{formatCurrency(order.total)}</span>
                        <Badge className={order.payment_status === 'unpaid' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}>
                          {order.payment_status}
                        </Badge>
                        <Button 
                          size="sm"
                          onClick={() => {
                            setSelectedOrder(order);
                            const dueDate = new Date();
                            dueDate.setDate(dueDate.getDate() + 30);
                            setInvoiceForm({
                              ...invoiceForm,
                              due_date: dueDate.toISOString().split('T')[0]
                            });
                            setShowInvoiceDialog(true);
                          }}
                          data-testid={`create-invoice-${order.id}`}
                        >
                          <Receipt className="h-4 w-4 mr-1" />
                          Create Invoice
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-2 text-green-300" />
                  <p>All orders have been invoiced</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Invoices */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Recent Invoices</CardTitle>
            </CardHeader>
            <CardContent>
              {arData?.invoices?.length > 0 ? (
                <ScrollArea className="h-64">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Invoice #</th>
                        <th className="text-left py-2">Contact</th>
                        <th className="text-right py-2">Total</th>
                        <th className="text-right py-2">Balance</th>
                        <th className="text-center py-2">Status</th>
                        <th className="text-right py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {arData.invoices.map(inv => (
                        <tr key={inv.id} className="border-b">
                          <td className="py-2 font-medium">{inv.invoice_number}</td>
                          <td className="py-2">{inv.contact_name}</td>
                          <td className="py-2 text-right">{formatCurrency(inv.total)}</td>
                          <td className="py-2 text-right font-medium">{formatCurrency(inv.balance_due)}</td>
                          <td className="py-2 text-center">
                            <Badge className={
                              inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                              inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                              inv.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {inv.status}
                            </Badge>
                          </td>
                          <td className="py-2 text-right">
                            <div className="flex justify-end gap-1">
                              {inv.status === 'draft' && (
                                <Button size="sm" variant="ghost" onClick={() => handleSendInvoice(inv.id)}>
                                  <Send className="h-4 w-4" />
                                </Button>
                              )}
                              {inv.balance_due > 0 && (
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => {
                                    const amount = prompt('Enter payment amount:', inv.balance_due);
                                    if (amount) handleRecordPayment(inv.id, parseFloat(amount));
                                  }}
                                >
                                  <CreditCard className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No invoices yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Create Invoice Dialog */}
          <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create Invoice</DialogTitle>
                <DialogDescription>
                  Create invoice for order {selectedOrder?.order_number}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Due Date</Label>
                    <Input
                      type="date"
                      value={invoiceForm.due_date}
                      onChange={(e) => setInvoiceForm({...invoiceForm, due_date: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Payment Terms</Label>
                    <Select
                      value={invoiceForm.payment_terms}
                      onValueChange={(v) => setInvoiceForm({...invoiceForm, payment_terms: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="net_0">Due on Receipt</SelectItem>
                        <SelectItem value="net_15">Net 15</SelectItem>
                        <SelectItem value="net_30">Net 30</SelectItem>
                        <SelectItem value="net_45">Net 45</SelectItem>
                        <SelectItem value="net_60">Net 60</SelectItem>
                        <SelectItem value="net_90">Net 90</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Tax Rate (%)</Label>
                  <Input
                    type="number"
                    value={invoiceForm.tax_rate}
                    onChange={(e) => setInvoiceForm({...invoiceForm, tax_rate: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={invoiceForm.notes}
                    onChange={(e) => setInvoiceForm({...invoiceForm, notes: e.target.value})}
                    placeholder="Optional notes for the invoice"
                  />
                </div>
                {selectedOrder && (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Order Total</p>
                    <p className="text-xl font-bold">{formatCurrency(selectedOrder.total)}</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowInvoiceDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateInvoice}>Create Invoice</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Budget Templates Tab */}
        <TabsContent value="budgets" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Budget Templates & Planning</h3>
            <div className="flex gap-2">
              <Button variant="outline" onClick={handleDownloadBudgetTemplate}>
                <Download className="h-4 w-4 mr-2" />
                Download Template
              </Button>
              <Button variant="outline" onClick={() => setShowAIBudgetDialog(true)} data-testid="ai-generate-btn">
                <Sparkles className="h-4 w-4 mr-2" />
                AI Generate
              </Button>
              <Button onClick={() => setShowBudgetDialog(true)} data-testid="new-budget-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Budget
              </Button>
            </div>
          </div>

          {/* Budget Templates List */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {budgetTemplates.map(template => (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-base">{template.name}</CardTitle>
                      <CardDescription>{template.template_type} • FY{template.fiscal_year}</CardDescription>
                    </div>
                    <Badge className={
                      template.status === 'active' ? 'bg-green-100 text-green-700' :
                      template.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                      'bg-amber-100 text-amber-700'
                    }>
                      {template.status || 'draft'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-500 mb-3">
                    {template.line_items?.length || 0} line items
                    {template.is_template && ' • Template'}
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Button 
                      size="sm" 
                      onClick={() => window.location.href = `/finance/budget/${template.id}`}
                      data-testid={`edit-budget-${template.id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      Edit Budget
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleExportBudgetExcel(template.id)}>
                      <FileSpreadsheet className="h-4 w-4 mr-1" />
                      Excel
                    </Button>
                    {template.is_template && (
                      <Button size="sm" variant="outline">
                        <Copy className="h-4 w-4 mr-1" />
                        Clone
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteBudget(template.id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {budgetTemplates.length === 0 && (
              <Card className="col-span-full">
                <CardContent className="py-12 text-center text-gray-500">
                  <Wallet className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No budget templates yet</p>
                  <p className="text-sm">Create a new budget or use AI to generate one</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* AI Budget Generator Dialog */}
          <Dialog open={showAIBudgetDialog} onOpenChange={setShowAIBudgetDialog}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-purple-500" />
                  AI Budget Generator
                </DialogTitle>
                <DialogDescription>
                  Let AI help you create a comprehensive budget template
                </DialogDescription>
              </DialogHeader>
              
              {!generatedBudget ? (
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Budget Type</Label>
                      <Select
                        value={aiBudgetForm.budget_type}
                        onValueChange={(v) => setAiBudgetForm({...aiBudgetForm, budget_type: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="marketing">Marketing</SelectItem>
                          <SelectItem value="sales">Sales</SelectItem>
                          <SelectItem value="operations">Operations</SelectItem>
                          <SelectItem value="hr">HR / People</SelectItem>
                          <SelectItem value="it">IT / Technology</SelectItem>
                          <SelectItem value="general">General / Company-wide</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Fiscal Year</Label>
                      <Input
                        type="number"
                        value={aiBudgetForm.fiscal_year}
                        onChange={(e) => setAiBudgetForm({...aiBudgetForm, fiscal_year: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Total Annual Budget ($)</Label>
                      <Input
                        type="number"
                        value={aiBudgetForm.total_budget}
                        onChange={(e) => setAiBudgetForm({...aiBudgetForm, total_budget: parseFloat(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Company Size</Label>
                      <Select
                        value={aiBudgetForm.company_size}
                        onValueChange={(v) => setAiBudgetForm({...aiBudgetForm, company_size: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="startup">Startup (&lt;10)</SelectItem>
                          <SelectItem value="small">Small (10-50)</SelectItem>
                          <SelectItem value="medium">Medium (50-200)</SelectItem>
                          <SelectItem value="large">Large (200-1000)</SelectItem>
                          <SelectItem value="enterprise">Enterprise (1000+)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Industry (optional)</Label>
                    <Input
                      value={aiBudgetForm.industry}
                      onChange={(e) => setAiBudgetForm({...aiBudgetForm, industry: e.target.value})}
                      placeholder="e.g., SaaS, Manufacturing, Retail"
                    />
                  </div>
                  <div>
                    <Label>Additional Notes</Label>
                    <Textarea
                      value={aiBudgetForm.notes}
                      onChange={(e) => setAiBudgetForm({...aiBudgetForm, notes: e.target.value})}
                      placeholder="Any specific requirements or focus areas..."
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-4 py-4">
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-semibold text-green-800">{generatedBudget.generated_budget?.name}</h4>
                    <p className="text-sm text-green-600">{generatedBudget.generated_budget?.description}</p>
                  </div>
                  <div>
                    <Label>Generated Line Items ({generatedBudget.generated_budget?.line_items?.length || 0})</Label>
                    <ScrollArea className="h-48 border rounded-lg p-2 mt-1">
                      {generatedBudget.generated_budget?.line_items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between py-1 border-b last:border-0">
                          <span className="text-sm">{item.category}: {item.description}</span>
                          <span className="text-sm font-medium">
                            {formatCurrency(Object.values(item.monthly_amounts || {}).reduce((a, b) => a + b, 0))}/yr
                          </span>
                        </div>
                      ))}
                    </ScrollArea>
                  </div>
                  {generatedBudget.recommendations?.length > 0 && (
                    <div>
                      <Label>AI Recommendations</Label>
                      <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                        {generatedBudget.recommendations.map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
              
              <DialogFooter>
                {!generatedBudget ? (
                  <>
                    <Button variant="outline" onClick={() => setShowAIBudgetDialog(false)}>Cancel</Button>
                    <Button onClick={handleGenerateAIBudget} disabled={generatingAI}>
                      {generatingAI ? (
                        <>
                          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="h-4 w-4 mr-2" />
                          Generate Budget
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setGeneratedBudget(null)}>
                      Regenerate
                    </Button>
                    <Button onClick={handleSaveGeneratedBudget}>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Save Budget Template
                    </Button>
                  </>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* New Budget Dialog */}
          <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Budget</DialogTitle>
                <DialogDescription>
                  Create a new budget or reusable template
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Budget Name *</Label>
                  <Input
                    value={newBudgetForm.name}
                    onChange={(e) => setNewBudgetForm({...newBudgetForm, name: e.target.value})}
                    placeholder="e.g., Marketing Budget 2026"
                    data-testid="budget-name-input"
                  />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={newBudgetForm.description}
                    onChange={(e) => setNewBudgetForm({...newBudgetForm, description: e.target.value})}
                    placeholder="Brief description of this budget"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Budget Type</Label>
                    <Select
                      value={newBudgetForm.template_type}
                      onValueChange={(v) => setNewBudgetForm({...newBudgetForm, template_type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="marketing">Marketing</SelectItem>
                        <SelectItem value="sales">Sales</SelectItem>
                        <SelectItem value="operations">Operations</SelectItem>
                        <SelectItem value="hr">HR / People</SelectItem>
                        <SelectItem value="it">IT / Technology</SelectItem>
                        <SelectItem value="general">General</SelectItem>
                        <SelectItem value="departmental">Departmental</SelectItem>
                        <SelectItem value="project">Project</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fiscal Year</Label>
                    <Input
                      type="number"
                      value={newBudgetForm.fiscal_year}
                      onChange={(e) => setNewBudgetForm({...newBudgetForm, fiscal_year: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Save as Reusable Template</Label>
                    <p className="text-sm text-gray-500">Can be cloned for future years</p>
                  </div>
                  <Switch
                    checked={newBudgetForm.is_template}
                    onCheckedChange={(c) => setNewBudgetForm({...newBudgetForm, is_template: c})}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowBudgetDialog(false)}>Cancel</Button>
                <Button onClick={handleCreateBudget} disabled={!newBudgetForm.name} data-testid="create-budget-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Budget
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          {/* Scheduler Status Card */}
          <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-900">Automated Variance Checker</h4>
                    <p className="text-sm text-blue-700">
                      Status: <span className="font-medium text-green-600">Running</span> • 
                      Checks every hour • 
                      {schedulerStatus?.alerts?.total_active || 0} active alert{schedulerStatus?.alerts?.total_active !== 1 ? 's' : ''} 
                      ({schedulerStatus?.alerts?.by_frequency?.daily || 0} daily, {schedulerStatus?.alerts?.by_frequency?.weekly || 0} weekly, {schedulerStatus?.alerts?.by_frequency?.monthly || 0} monthly)
                    </p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  onClick={handleRunScheduledCheck}
                  disabled={runningScheduledCheck}
                  className="bg-white"
                  data-testid="run-scheduled-check-btn"
                >
                  {runningScheduledCheck ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Run Scheduled Check
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Variance Alert Configuration</h3>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleCheckAlerts}
                disabled={checkingAlerts}
                data-testid="check-alerts-btn"
              >
                {checkingAlerts ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Check Now
              </Button>
              <Button 
                variant="outline"
                onClick={handleDownloadVariancePdf}
                disabled={generatingPdf}
                data-testid="download-variance-pdf-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export PDF
              </Button>
              <Dialog open={showNewAlertDialog} onOpenChange={setShowNewAlertDialog}>
                <DialogTrigger asChild>
                  <Button data-testid="new-alert-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Alert
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create Variance Alert</DialogTitle>
                    <DialogDescription>
                      Set up automatic alerts when forecast variance exceeds threshold
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div>
                      <Label>Alert Name</Label>
                      <Input
                        value={newAlert.name}
                        onChange={(e) => setNewAlert({ ...newAlert, name: e.target.value })}
                        placeholder="e.g., Revenue Drop Alert"
                        data-testid="alert-name-input"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={newAlert.description}
                        onChange={(e) => setNewAlert({ ...newAlert, description: e.target.value })}
                        placeholder="Optional description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Alert Type</Label>
                        <Select
                          value={newAlert.alert_type}
                          onValueChange={(v) => setNewAlert({ ...newAlert, alert_type: v })}
                        >
                          <SelectTrigger data-testid="alert-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="revenue">Revenue</SelectItem>
                            <SelectItem value="expense">Expense</SelectItem>
                            <SelectItem value="margin">Margin</SelectItem>
                            <SelectItem value="unit_sales">Unit Sales</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Threshold %</Label>
                        <Input
                          type="number"
                          value={newAlert.threshold_percentage}
                          onChange={(e) => setNewAlert({ ...newAlert, threshold_percentage: parseFloat(e.target.value) })}
                          data-testid="threshold-input"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Direction</Label>
                        <Select
                          value={newAlert.threshold_direction}
                          onValueChange={(v) => setNewAlert({ ...newAlert, threshold_direction: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="both">Above or Below</SelectItem>
                            <SelectItem value="above">Above Only</SelectItem>
                            <SelectItem value="below">Below Only</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Check Frequency</Label>
                        <Select
                          value={newAlert.check_frequency}
                          onValueChange={(v) => setNewAlert({ ...newAlert, check_frequency: v })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Active</Label>
                      <Switch
                        checked={newAlert.is_active}
                        onCheckedChange={(c) => setNewAlert({ ...newAlert, is_active: c })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowNewAlertDialog(false)}>Cancel</Button>
                    <Button onClick={handleCreateAlert} disabled={!newAlert.name} data-testid="create-alert-btn">
                      Create Alert
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Triggered Alerts */}
          {triggeredAlerts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Triggered Alerts ({triggeredAlerts.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {triggeredAlerts.map(trigger => (
                    <div key={trigger.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge className={getSeverityColor(trigger.severity)}>
                          {trigger.severity}
                        </Badge>
                        <div>
                          <p className="font-medium">{trigger.alert_name}</p>
                          <p className="text-sm text-gray-500">
                            Period: {trigger.period} | {trigger.metric_type}: {formatCurrency(trigger.actual_value)} vs {formatCurrency(trigger.forecast_value)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`font-bold ${trigger.variance_percentage < 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {trigger.variance_percentage > 0 ? '+' : ''}{trigger.variance_percentage}%
                        </span>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handleAcknowledgeAlert(trigger.id)}
                          data-testid={`acknowledge-${trigger.id}`}
                        >
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Alert Configurations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Alert Configurations</CardTitle>
            </CardHeader>
            <CardContent>
              {alerts.length > 0 ? (
                <div className="space-y-3">
                  {alerts.map(alert => (
                    <div key={alert.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`h-3 w-3 rounded-full ${alert.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <div>
                          <p className="font-medium">{alert.name}</p>
                          <p className="text-sm text-gray-500">
                            {alert.alert_type} | Threshold: ±{alert.threshold_percentage}% | {alert.check_frequency}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleDeleteAlert(alert.id)}
                          data-testid={`delete-alert-${alert.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Bell className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No variance alerts configured</p>
                  <p className="text-sm">Create an alert to monitor forecast variances</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cash Flow Tab */}
        <TabsContent value="cashflow" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">12-Month Cash Flow Projection</CardTitle>
              <CardDescription>Based on active forecast and budget data</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="w-full">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Period</th>
                      <th className="text-right py-2 px-3">Opening</th>
                      <th className="text-right py-2 px-3">Inflows</th>
                      <th className="text-right py-2 px-3">Outflows</th>
                      <th className="text-right py-2 px-3">Net Flow</th>
                      <th className="text-right py-2 px-3">Closing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cashFlow.map((cf, idx) => (
                      <tr key={cf.period} className={idx % 2 === 0 ? 'bg-gray-50' : ''}>
                        <td className="py-2 px-3 font-medium">{cf.period}</td>
                        <td className="text-right py-2 px-3">{formatCurrency(cf.opening_balance)}</td>
                        <td className="text-right py-2 px-3 text-green-600">{formatCurrency(cf.cash_inflows)}</td>
                        <td className="text-right py-2 px-3 text-red-600">{formatCurrency(cf.cash_outflows)}</td>
                        <td className={`text-right py-2 px-3 font-medium ${cf.net_cash_flow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(cf.net_cash_flow)}
                        </td>
                        <td className="text-right py-2 px-3 font-medium">{formatCurrency(cf.closing_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={handleDownloadForecastPdf}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <FileText className="h-12 w-12 mx-auto mb-3 text-blue-500" />
                  <h3 className="font-semibold">Forecast Report</h3>
                  <p className="text-sm text-gray-500 mt-1">Complete sales forecast with letterhead</p>
                  <Button className="mt-4" variant="outline" disabled={generatingPdf || !dashboard?.active_forecast?.id}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={handleDownloadVariancePdf}>
              <CardContent className="pt-6">
                <div className="text-center">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-amber-500" />
                  <h3 className="font-semibold">Variance Report</h3>
                  <p className="text-sm text-gray-500 mt-1">All triggered variance alerts</p>
                  <Button className="mt-4" variant="outline" disabled={generatingPdf}>
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <BarChart3 className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                  <h3 className="font-semibold">Budget vs Actual</h3>
                  <p className="text-sm text-gray-500 mt-1">Coming soon</p>
                  <Button className="mt-4" variant="outline" disabled>
                    <Clock className="h-4 w-4 mr-2" />
                    Coming Soon
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinanceDashboardPage;
